import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceStatus } from '../../types';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { getStatusKey } from '../../utils/formatters';

interface StatusChangeNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetStatus: InvoiceStatus | null;
  onConfirm: (comment: string) => void;
}

export const StatusChangeNoteDialog: React.FC<StatusChangeNoteDialogProps> = ({
  open,
  onOpenChange,
  targetStatus,
  onConfirm,
}) => {
  const { t } = useTranslation('invoices');
  const { t: tCommon } = useTranslation('common');
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (open) {
      setComment('');
    }
  }, [open]);

  const isRequired = targetStatus === InvoiceStatus.REJECTED;
  const isConfirmDisabled = isRequired && comment.trim().length === 0;
  const statusLabel = targetStatus ? tCommon(`status.${getStatusKey(targetStatus)}`) : '';

  const handleConfirm = () => {
    onConfirm(comment.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('statusChangeNote.title')}</DialogTitle>
          <DialogDescription>
            {t('statusChangeNote.description', { status: statusLabel })}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="reviewer-comment">
              {t('statusChangeNote.label')}
              {isRequired
                ? ` (${t('statusChangeNote.required')})`
                : ` (${t('statusChangeNote.optional')})`}
            </Label>
            <Textarea
              id="reviewer-comment"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                isRequired
                  ? t('statusChangeNote.placeholderRequired')
                  : t('statusChangeNote.placeholderOptional')
              }
            />
            {isRequired && comment.trim().length === 0 && (
              <p className="text-xs text-destructive">
                {t('statusChangeNote.validationRequired')}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {tCommon('buttons.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={isConfirmDisabled}>
            {tCommon('buttons.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
